/**
 * The two ends of a STEP — opening it and closing it — as the machinery every job type shares, with nothing in here
 * about which step is which.
 *
 * A step is finished when the exam has no live job of that step's types left (`STEP_JOB_TYPES`). That is the whole
 * model, and it is what replaced a per-run `batchKey`: a run was defined as open exactly while one of its rows was
 * outstanding, and the key was minted by asking that same question, so the label between the two was never load
 * bearing. What it cost is listed on `anyLiveJobForTypes`.
 *
 * It also replaced three hand-rolled versions of the same idea before that: `completeRunIfDone` in the two
 * generation jobs, which re-read the whole task tree on every root's completion, and `announceIfSetupRunFinished`
 * in the answer-extraction job, which compared two counters on the exam. All three answered "has the work
 * finished?" by re-deriving it from the domain — expensive, and a second place to get it wrong when the queue
 * already knows.
 *
 * WHERE THE RULES LIVE, since they are deliberately not here. Each job type DECLARES `onStepComplete` (see
 * `StepCompletion`), and where the declaration is written follows from how many job types produce that step: one
 * producer writes it in the job type, several share one object from `steps/*.step.ts`.
 *
 * This file is the "how" and nothing else. It has shed a layer at each step: first a `CLOSURES` map and a
 * `dispatchFollowUp` switch, so answering "what happens when a master solution finishes?" took four locations; then
 * the `setupStepSync(closure)` wrapper each type applied by hand, and the `step` name each closure restated. What is
 * left is one exported function, called from one place.
 *
 * WHAT IT OWNS AND WHAT IT DOES NOT. It owns the step's CLOSING — the IN_PROGRESS → COMPLETED transition and the
 * announcement that follows it. It deliberately does not touch the progress record's unit counters
 * (`total`/`finishedUnits`, `createdForTasks`), because those count WORK UNITS while this counts JOBS, and the two
 * only coincide by accident:
 *
 *  - `task` is one job over every NODE of the tree — 1 row for 40 units.
 *  - `masterSolution`/`gradingScheme` count solution-unit roots as a SET of task keys, which is already idempotent
 *    under a requeue and can be walked backwards by a refine; a job count cannot express either.
 *  - `answer` is one job per submission, but its record is cumulative ACROSS rounds (a re-upload adds to the same
 *    total) — so recomputing from the queue would reset the bar every round.
 *
 * Writing job counts into those fields would make the queue the authority on the progress bar, which is the
 * opposite of the design this file sits in. The state stays the source of truth; the queue answers exactly one
 * question, "is any machine work for this step still outstanding?", and that answer is what closes it.
 *
 * WHERE IT RUNS. From the transition hook, not from the handlers. A handler runs BEFORE its own row is written to a
 * terminal status (see `runner.ts` — the outcome is captured, then `completeJob` writes it), so the predicate
 * inside a handler always counts its own row as live and the last job of a step can never close it. That was a live
 * bug in the `announceIfSetupRunFinished` this replaces. The transition hook fires after the write, which is the
 * first moment the question has a correct answer.
 *
 * THE ONE HAZARD, unchanged by any of the simplifications and worth stating: "am I last?" is asked of a queue that
 * can gain a row a millisecond later, so a step can close and then reopen when the next pass arrives. That was
 * equally true of batches — a row enqueued after a batch drained minted a NEW one and reopened the record
 * identically — so it is a property of derived completion, not a cost of removing the key.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import {
  beginProgressRun,
  isSolutionArtifact,
  progressRecordOf,
  seedProgressRecord,
  setSolutionStatus,
  setStatusByExam,
  StepState,
} from '../../extraction-progress.js';
import { updateSetupStep } from '../pending-tasks/extraction-or-generation-finished.js';
import { claimBatchCompletion, closeBatchQuietly } from './batch-store.js';
import { anyLiveJobForTypes, jobsOfBatch } from './job-store.js';
import { batchScopeOf, setupStepOf, stepTypesOf, type DispatchScope, type SetupStep } from './job.js';
import type { BatchOutcome, FinishedRun, StepCompletion, StepStart } from './step.js';

/**
 * Opens a type's step: its own progress run, any records it wants to exist alongside, then whatever it writes itself.
 *
 * ORDER IS THE POINT, and it is why this is one function rather than three lines at each dispatch. `beginProgressRun`
 * comes first because it seeds this step's record and clears the fields that describe one pass; the seeds come next
 * because they must not disturb a record another step already opened; `open` comes last because it is the call that
 * makes the step read as IN_PROGRESS, and a row that finished before it landed would try to close a step the record
 * still called NOT_STARTED.
 *
 * A type with no setup step (`grading`, `submissionSplit`) has nothing to open — their progress is a session refcount
 * and a pair of records the work itself writes — so they declare nothing and this is never called for them.
 */
export async function runStepStart(
  type: string,
  start: StepStart,
  examId: string,
  scope: DispatchScope,
): Promise<void> {
  const step = setupStepOf(type);
  if (step) {
    // A value or a question about the scope — see `StepStart.reupload`. `undefined` from either form means "let
    // `beginProgressRun` derive it", so the fallback is one branch rather than one per form.
    const reupload = typeof start.reupload === 'function' ? start.reupload(scope) : start.reupload;
    await beginProgressRun(examId, step, reupload === undefined ? {} : { reupload });
  }

  // Concurrently, because each is one `updateOne` against a different field and none of them reads another.
  await Promise.all((start.seed ?? []).map(other => seedProgressRecord(examId, other)));

  if (start.open) await start.open(examId, scope);
}

/**
 * May this record be closed by this declaration? False for a step that never opened a run, one that has already
 * recorded its own failure, and one whose own precondition says the step is not finished after all.
 *
 * ERROR is a verdict the step itself wrote about its own outcome; overwriting it with a success is how a failed
 * extraction gets announced as a finished one.
 *
 * COMPLETED is allowed through rather than treated as "someone got here first", because for the EXTRACTION steps it is
 * the normal state on arrival: `runMasterSolutionStep` and `runGradingSchemeStep` close their own record, one job per
 * run, so this is only ever closing the ANNOUNCEMENT for them. Re-announcing is what `updateSetupStep` absorbs.
 *
 * NOT_STARTED IS CLOSEABLE ONLY AS A FAILURE, and that exception is the whole fix for a step that failed silently.
 * `msExtraction` and `gsExtraction` write their status from the HANDLER, so a run that dies before reaching
 * `startSolutionProgress` — an unreadable exam, a missing task tree, an OCR call that throws immediately — leaves the
 * record exactly as the dispatch found it. All three attempts then burn, the wave ends, and a predicate that refused
 * NOT_STARTED outright wrote nothing at all: the bar sat on pending forever and the teacher was never told. A wave
 * that produced NOTHING and failed is the one case where an unopened record still has something true to say.
 *
 * NO NEWER-RUN GUARD, because there are no runs on the rows to be newer than. It used to compare the record's `runId`
 * against the finishing pass's, since a second pass could open its own run while this one drained and closing the
 * record would then report the old outcome over the live one. The predicate is "does this exam have any live job of
 * this step?", which a second pass keeps false by simply existing.
 */
function recordIsCloseable(
  record: Record<string, any>,
  examState: any,
  completion: StepCompletion,
  failedOutright: boolean,
): boolean {
  const closeableStatuses: string[] = failedOutright
    ? [StepState.QUEUED, StepState.IN_PROGRESS, StepState.COMPLETED, StepState.NOT_STARTED]
    : [StepState.QUEUED, StepState.IN_PROGRESS, StepState.COMPLETED];
  if (!closeableStatuses.includes(record.status)) return false;
  const precondition = completion.progress?.precondition;
  return !precondition || precondition(examState, record);
}

/** A wave that produced nothing and had failures — see `recordIsCloseable` and `writeCompletedStatus`. */
const waveFailedOutright = (wave: BatchOutcome): boolean => wave.failed.length > 0 && wave.succeeded.length === 0;

/** IN_PROGRESS → COMPLETED, by the writer the step's kind requires. A record already COMPLETED is left alone. */
async function writeCompletedStatus(
  examId: string,
  step: SetupStep,
  progress: NonNullable<StepCompletion['progress']>,
  wave: BatchOutcome,
): Promise<void> {
  // A WAVE THAT PRODUCED NOTHING AND FAILED IS AN ERROR, not a completion — the one place the wave's aggregate is
  // used for the step's own state rather than for its announcement. It is the other half of the silent-failure fix:
  // `recordIsCloseable` lets an unopened record through for exactly this case, and this is what it then says.
  //
  // PARTIAL SUCCESS IS STILL A COMPLETION, deliberately: eight of ten submissions extracted is a finished step whose
  // announcement names the two that failed (`erroredStudents`), not a failed step.
  const failedOutright = waveFailedOutright(wave);
  const status = failedOutright ? StepState.ERROR : StepState.COMPLETED;
  const message = failedOutright ? (progress.errorMessage ?? progress.message) : progress.message;

  if (failedOutright) {
    logger.warn(
      { examId, step, failed: wave.failed.length, batchKey: wave.batchKey },
      'Step wave produced nothing and had failures — closing it as ERROR',
    );
  }

  if (isSolutionArtifact(step)) {
    await setSolutionStatus(examId, step, status, message);
  } else {
    await setStatusByExam(examId, step, status, message);
  }
}

/**
 * Closes the step's progress record, and says whether the wave may go on to announce.
 *
 * FALSE stops the completion entirely: a step that declares `progress` and has no record never opened a run, and one
 * whose precondition or recorded verdict refuses the close is not finished after all.
 *
 * The write itself is skipped for a record already COMPLETED — the extraction steps' normal arrival state, since they
 * close their own, so for them this is only ever closing the ANNOUNCEMENT. An ERROR the step wrote about itself
 * survives because `recordIsCloseable` refuses that one outright.
 */
async function closeRecord(
  run: { examId: string; step: SetupStep | undefined; record: Record<string, any> | null | undefined; examState: any },
  completion: StepCompletion,
  wave: BatchOutcome,
): Promise<boolean> {
  const { examId, step, record, examState } = run;
  if (!step || !record || !completion.progress) return false;
  const failedOutright = waveFailedOutright(wave);
  if (!recordIsCloseable(record, examState, completion, failedOutright)) return false;
  if (record.status !== StepState.COMPLETED) {
    await writeCompletedStatus(examId, step, completion.progress, wave);
  }
  return true;
}

/**
 * Takes the right to run this wave's completion, and reads what the wave consisted of.
 *
 * `null` means somebody else won the latch — every row of a drained wave gets here and exactly one may act.
 *
 * A ROW WITH NO BATCH still gets its completion, with an empty outcome. That is the rows that were already queued
 * when batching shipped: they carry no key, so there is nothing to latch on, and the old behaviour — run it, relying
 * on each write being idempotent — is exactly right for them. Skipping instead would leave those steps unable to
 * close at all, which is a deploy that hangs every exam mid-flight.
 */
async function claimWave(examId: string, step: string, batchKey: string | undefined): Promise<BatchOutcome | null> {
  const empty: BatchOutcome = { batchKey: '', intents: [], succeeded: [], failed: [] };
  if (!batchKey) return empty;

  if (!(await claimBatchCompletion(examId, step, batchKey))) return null;

  const rows = await jobsOfBatch(batchKey);
  return {
    batchKey,
    // THE ASKS, READ OFF THE ROWS. They used to live on a batch document; a row carries its own, and a trigger whose
    // units all deduped has its cause pushed onto the row that absorbed it — so the rows ARE the record of the wave
    // and nothing has to be kept in step with them.
    intents: rows.flatMap(row => (Array.isArray(row.intents) ? row.intents : [])),
    // SUCCEEDED means `done` and not skipped: a skipped row reports that its work was unnecessary, which is not the
    // same as having done it, and an announcement built from skipped rows names units nothing happened to.
    succeeded: rows.filter(row => row.status === 'done' && !row.skipped),
    // NOT `cancelled`: a dropped row's own contract is "the reason to run is gone... terminal, but not a failure"
    // (see `cancelJob`) — a scheme/solution with nothing uploaded drops its extraction row for exactly that reason.
    // Counting it here made `waveFailedOutright` fire for a wave that never even tried, which closed the step as
    // ERROR and then announced a nonexistent extraction as "completed" (`producerOf` reads the unset producer that
    // leaves as `extraction`). A genuine failure always reaches `failed` via `failJob` after attempts are exhausted,
    // never via `cancelJob`, so this excludes only true no-ops.
    failed: rows.filter(row => row.status === 'failed'),
  };
}

/**
 * Ends a wave: closes its batch and, for a type that declares one, runs its `onStepComplete` — exactly once.
 *
 * THE CHEAP QUESTION COMES FIRST. Every row of every type reaches this and for all but the last of a wave the answer
 * is "not yet", so it is one indexed exists-query before any document read.
 *
 * THE BATCH IS THE LATCH, and that is what changed here. Every row of a drained wave reaches this and all of them
 * compute the same answer, so exactly one may act on it — `claimBatchCompletion` closes the batch and takes the right
 * to announce in one atomic write, and everybody else gets `null` and stops. This used to rely on every write below
 * being individually idempotent, which held for a status `$set` and for `updateSetupStep` but NOT for `grading`'s
 * `finish`: reading a ledger and clearing it is a read-then-write, and two rows finishing together both read it. One
 * latch is a stronger guarantee than N separately-argued idempotencies, and it is the piece rows cannot supply
 * themselves.
 *
 * A TYPE WITH NO DECLARATION STILL CLOSES ITS BATCH. An open batch is what the next dispatch JOINS, so one left open
 * after its work finished folds the next, unrelated pass into it — `structure` and `submissionSplit` reach only that.
 */
export async function finishStepWave(
  type: string,
  completion: StepCompletion | undefined,
  examId: string,
  batchKey?: string,
): Promise<void> {
  if (!examId) return;
  if (await anyLiveJobForTypes(examId, stepTypesOf(type))) return;

  const batchScope = batchScopeOf(type);

  if (!completion) {
    if (batchKey) await closeBatchQuietly(examId, batchScope, batchKey);
    return;
  }

  const outcome = await claimWave(examId, batchScope, batchKey);
  // Another row of this wave won the latch and is running the completion. Ordinary, and the reason there is a latch.
  if (!outcome) return;

  const step = setupStepOf(type);

  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any, { projection: { examState: 1 } });
  const examState = (exam as any)?.examState;

  // A step with no progress record — `grading` — has nothing to gate on, so `finish` and `followUp` are all there is.
  const record = completion.progress && step ? progressRecordOf(examState, step) : undefined;
  if (completion.progress && !(await closeRecord({ examId, step, record, examState }, completion, outcome))) return;

  logger.info({ examId, step: step ?? type }, 'Step complete — no jobs of it left for this exam');

  const changedTasks = Array.isArray(record?.changedTasks) ? (record.changedTasks as string[]) : [];
  await releaseAndAnnounce({ examId, examState, changedTasks, batch: outcome }, completion, record);
}

/**
 * The two things a closed step does next, in the order they have to happen.
 *
 * `finish` FIRST, because it is the step closing itself rather than releasing anything: `grading` ends its session and
 * announces there, and a follow-up that dispatched more grading would otherwise see a session this wave had not yet
 * closed.
 *
 * The announcement comes AFTER the status write in the caller, so the gate inside `updateSetupStep` sees this step as
 * finished. That gate — not anything here — is what holds the announcement back while a SIBLING step is still running,
 * which is why a per-step declaration needs no cross-step guard of its own.
 *
 * It is suppressed when the follow-up reports that it dispatched something: that work announces its own result, and
 * announcing twice for one upload is what the return value exists to prevent.
 */
async function releaseAndAnnounce(
  run: FinishedRun,
  completion: StepCompletion,
  record: Record<string, any> | null | undefined,
): Promise<void> {
  if (completion.finish) await completion.finish(run);
  if (completion.followUp && (await completion.followUp(run))) return;

  const delta = record && completion.progress ? completion.progress.delta(record) : null;
  if (delta) await updateSetupStep(run.examId, delta);
}
